import React from 'react';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { HtmlContent } from '../../utils/html_content';
import isExpertIcon from '../../../img/product/is_expert.svg';

/**
 * SVG component for low risk level (circular shape)
 * viewBox: 33x32, center: (16.5, 16)
 */
const LowRiskSvg = ({ img, width = 33, height = 32, imageWidth = 11, imageHeight = 11 }) => {
  // Center calculation: x = (33 - imageWidth) / 2, y = (32 - imageHeight) / 2
  const x = (33 - imageWidth) / 2;
  const y = (32 - imageHeight) / 2;
  return (
    <svg width={width} height={height} viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M16.5 30C24.5666 30 31 23.6744 31 16C31 8.32558 24.5666 2 16.5 2C8.43341 2 2 8.32558 2 16C2 23.6744 8.43341 30 16.5 30ZM16.5 32C25.6127 32 33 24.8366 33 16C33 7.16344 25.6127 0 16.5 0C7.3873 0 0 7.16344 0 16C0 24.8366 7.3873 32 16.5 32Z" fill="#389E0D" />
      {img && <image href={img} x={x} y={y} width={imageWidth} height={imageHeight} preserveAspectRatio="xMidYMid meet" />}
    </svg>
  );
};

/**
 * SVG component for slight risk level (half-circle shape)
 * viewBox: 33x32, center: (16.5, 16)
 */
const SlightRiskSvg = ({ img, width = 33, height = 32, imageWidth = 11, imageHeight = 11 }) => {
  // Center calculation: x = (33 - imageWidth) / 2, y = (32 - imageHeight) / 2
  const x = (33 - imageWidth) / 2;
  const y = (32 - imageHeight) / 2;
  return (
    <svg width={width} height={height} viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M30.5 2H16.5C8.76801 2 2.5 8.26801 2.5 16C2.5 23.732 8.76801 30 16.5 30C24.232 30 30.5 23.732 30.5 16V2ZM16.5 0C7.66344 0 0.5 7.16344 0.5 16C0.5 24.8366 7.66344 32 16.5 32C25.3366 32 32.5 24.8366 32.5 16V0H16.5Z" fill="#389E0D" />
      {img && <image href={img} x={x} y={y} width={imageWidth} height={imageHeight} preserveAspectRatio="xMidYMid meet" />}
    </svg>
  );
};

/**
 * SVG component for moderate risk level (triangle shape pointing down)
 * viewBox: 41x32, triangle centroid is in upper portion
 * For inverted triangle, visual center is around y=10 (upper third where there's more space)
 */
const ModerateRiskSvg = ({ img, width = 41, height = 32, imageWidth = 11, imageHeight = 11 }) => {
  // Horizontal center: x = (41 - imageWidth) / 2
  // Vertical: position in upper portion of triangle (y=5 to shift towards top)
  const x = (41 - imageWidth) / 2;
  const y = 5;
  return (
    <svg width={width} height={height} viewBox="0 0 41 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M20.5 32L0.5 0H40.5L20.5 32ZM20.5 28.2425L4.11009 2.01863H36.8899L20.5 28.2425Z" fill="#FFC069" />
      {img && <image href={img} x={x} y={y} width={imageWidth} height={imageHeight} preserveAspectRatio="xMidYMid meet" />}
    </svg>
  );
};

/**
 * SVG component for high risk level (diamond shape)
 * viewBox: 33x32, center: (16.5, 16)
 */
const HighRiskSvg = ({ img, width = 33, height = 32, imageWidth = 11, imageHeight = 11 }) => {
  // Center calculation: x = (33 - imageWidth) / 2, y = (32 - imageHeight) / 2
  const x = (33 - imageWidth) / 2;
  const y = (32 - imageHeight) / 2;
  return (
    <svg width={width} height={height} viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M16.5 2.7859L2.87296 16L16.5 29.2141L30.127 16L16.5 2.7859ZM16.5 0L0 16L16.5 32L33 16L16.5 0Z" fill="#CF1322" />
      {img && <image href={img} x={x} y={y} width={imageWidth} height={imageHeight} preserveAspectRatio="xMidYMid meet" />}
    </svg>
  );
};

/**
 * SVG component for extremely high risk level (octagon shape)
 * viewBox: 32x32, center: (16, 16)
 */
const ExtremeRiskSvg = ({ img, width = 32, height = 32, imageWidth = 11, imageHeight = 11 }) => {
  // Center calculation: x = (32 - imageWidth) / 2, y = (32 - imageHeight) / 2
  const x = (32 - imageWidth) / 2;
  const y = (32 - imageHeight) / 2;
  return (
    <svg width={width} height={height} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M16 2.16478L6.21703 6.21703L2.16478 16L6.21703 25.783L16 29.8352L25.783 25.783L29.8352 16L25.783 6.21703L16 2.16478ZM27.3137 4.68629L16 0L4.68629 4.68629L0 16L4.68629 27.3137L16 32L27.3137 27.3137L32 16L27.3137 4.68629Z" fill="#FF4D4F" />
      {img && <image href={img} x={x} y={y} width={imageWidth} height={imageHeight} preserveAspectRatio="xMidYMid meet" />}
    </svg>
  );
};

// Map calculated_value (1-5) to SVG components
const RISK_COMPONENTS = {
  1: LowRiskSvg,
  2: SlightRiskSvg,
  3: ModerateRiskSvg,
  4: HighRiskSvg,
  5: ExtremeRiskSvg,
};

const SIZE_MAP = {
  sm: { width: 32, height: 32, imageWidth: 11, imageHeight: 11 },
  md: { width: 48, height: 48, imageWidth: 17, imageHeight: 17 },
  lg: { width: 64, height: 64, imageWidth: 23, imageHeight: 23 },
};

/**
 * Simple Risk Symbol Display
 * Uses calculated_value (1-5) to show the correct symbol
 * Uses display_label for the tooltip
 * Uses symbol.image for the image inside
 */
/**
 * SP-442 row 52 — render the limiting-criteria list with "50" as a subscript
 * (DL₅₀ orale, CL₅₀ inhalation …) and a comma separator. Criteria without a
 * "50" (long-term effects) pass through unchanged.
 */
const renderLimitingCriteria = (criteria) =>
  String(criteria)
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && ', '}
        {item.split(/(50)/).map((part, j) => (part === '50' ? <sub key={j}>50</sub> : part))}
      </React.Fragment>
    ));

const RiskSymbolDisplay = ({
  symbolData,
  size = 'md',
  hasBackground = true,
  hideWhenEmpty = false,
  expertMarker = null,
  professionalTooltip = null,
  criteriaLabel = null,
  nameOverride = null,
}) => {
  const { t } = useTranslation();

  // SP-442 row 46 — the risk calculator leaves the cell empty (not "N/A") when
  // no symbol can be displayed. Other tables keep the "N/A" default.
  const naFallback = hideWhenEmpty ? null : (
    <div className="text-gray-400 text-xs italic">N/A</div>
  );

  // Handle null/undefined
  if (!symbolData) return naFallback;

  // Extract data — when an expert has assigned a different symbol, use it for display
  const isExpertAssigned = symbolData.is_expert_assigned || symbolData.is_professional || false;
  const activeSymbol = (isExpertAssigned && symbolData.assigned_symbol) ? symbolData.assigned_symbol : symbolData.symbol;
  const calculatedValue = (isExpertAssigned && symbolData.assigned_symbol) ? (symbolData.assigned_symbol.label ?? 0) : (symbolData.calculated_value || 0);
  const displayLabel = activeSymbol?.display_label || '';
  const symbolType = activeSymbol?.type || symbolData.symbol?.type || symbolData.type || '';
  const image = activeSymbol?.image || null;
  // R26 — an attributed symbol (expert priority) displays the assigned symbol
  // (see activeSymbol l.150), so the data-derived limiting criteria no longer
  // describe it and must not appear in the tooltip. This front guard neutralises
  // the display regardless of the payload (Redis-cached or stale ES _source).
  const isAttributedSymbol = isExpertAssigned && symbolData.assigned_symbol;
  const limitingCriteria = isAttributedSymbol
    ? ''
    : (symbolData.limiting_criteria || symbolData.symbol?.limiting_criteria || '');
  const expertComment = symbolData.expert_comment || null;

  // Values < 1 are invalid, return N/A
  if (calculatedValue < 1) return naFallback;

  // Cap the display value to valid range (1-5)
  const displayValue = Math.min(5, calculatedValue);

  // Get SVG component
  const SvgComponent = RISK_COMPONENTS[displayValue];
  if (!SvgComponent) return naFallback;

  // Get size config
  const sizeConfig = SIZE_MAP[size] || SIZE_MAP.md;

  // Fallback label based on calculated risk level when symbol object is null
  const RISK_LEVEL_LABELS = {
    1: 'legends.risk.low',
    2: 'legends.risk.light',
    3: 'legends.risk.moderate',
    4: 'legends.risk.high',
    5: 'legends.risk.extremelyHigh',
  };
  const effectiveLabel = displayLabel || (RISK_LEVEL_LABELS[displayValue] ? t(`common|${RISK_LEVEL_LABELS[displayValue]}`) : '');

  // Build tooltip content. SP-442 rows 71/83 — the calculator overrides the
  // hover name for some symbols (Poisson → « Organismes aquatiques »,
  // Lessivage → « Mobilité »); other tables keep the symbolType name.
  const symbolTypeName =
    nameOverride || (symbolType ? t(`product-details|riskSymbol.symbolType.${symbolType}`) : '');
  const tooltipContent = (effectiveLabel || limitingCriteria) ? (
    <div>
      {effectiveLabel && (
        <div>{symbolTypeName ? `${symbolTypeName} : ${effectiveLabel}` : effectiveLabel}</div>
      )}
      {limitingCriteria && (
        <div style={{ marginTop: 4 }}>
          {/* SP-442 row 52 — "Critères limitants : DL₅₀ orale, …" in the
              calculator; the shared "= {criteria}" format elsewhere. */}
          {criteriaLabel ? (
            <>
              {criteriaLabel} {renderLimitingCriteria(limitingCriteria)}
            </>
          ) : (
            <>
              {t('product-details|riskSymbol.limitingCriteria')} = {limitingCriteria}
            </>
          )}
        </div>
      )}
    </div>
  ) : null;

  // Expert assignment indicator (works on hover + click for mobile).
  // SP-442 rows 50/51 — the calculator shows a superscript "a" (expertMarker)
  // with the professional-attribution note; other tables keep the icon badge.
  const expertTooltipText = professionalTooltip || t('common|riskSymbol.professionalAssignedTooltip');
  const expertBadge = isExpertAssigned ? (
    <Tooltip title={expertTooltipText} placement="top" trigger={['hover', 'click']}>
      {expertMarker ? (
        <sup className="absolute -top-2 -right-2 text-[10px] font-bold leading-none cursor-help">
          {expertMarker}
        </sup>
      ) : (
        <img src={isExpertIcon} alt="" className="absolute -top-[0.55rem] -right-1.5 w-3.5 h-3.5 cursor-help" />
      )}
    </Tooltip>
  ) : null;

  const symbolElement = (
    <div
      className="flex justify-center items-center"
      style={{
        width: sizeConfig.width,
        height: sizeConfig.height,
        backgroundColor: hasBackground ? '#F5F5F5' : 'transparent',
        borderRadius: hasBackground ? '4px' : '0',
        padding: hasBackground ? '4px' : '0',
      }}
    >
      <SvgComponent
        img={image}
        width={sizeConfig.width}
        height={sizeConfig.height}
        imageWidth={sizeConfig.imageWidth}
        imageHeight={sizeConfig.imageHeight}
      />
    </div>
  );

  return (
    <div className="relative inline-flex">
      {tooltipContent ? (
        <Tooltip title={tooltipContent} placement="top">
          {symbolElement}
        </Tooltip>
      ) : symbolElement}
      {expertBadge}
    </div>
  );
};

export default RiskSymbolDisplay;
